import type { Price } from "@spree/sdk";
import { formatMoney } from "./money";

type PriceLike = Pick<Price, "amount" | "compare_at_amount" | "currency">;

function amountOf(value: string | null | undefined): number | null {
  if (value == null || value === "") return null;
  const n = Number(value);
  return Number.isFinite(n) ? n : null;
}

/**
 * The price to show for a product or variant, and the higher price to strike
 * through when it's reduced: the original price-list price when that's
 * higher, otherwise the compare-at amount.
 *
 * Reads the decimal amounts. Spree 6.1 drops `display_amount` and the
 * `*_in_cents` fields this used to compare (#263).
 */
export function salePricing(
  price: PriceLike | null | undefined,
  originalPrice: PriceLike | null | undefined,
  locale: string,
): { price: string; strikethrough: string | null } {
  const shown = formatMoney(price?.amount, price?.currency, locale);
  const current = amountOf(price?.amount);
  const original = amountOf(originalPrice?.amount);
  const compareAt = amountOf(price?.compare_at_amount);

  const onSale =
    current != null &&
    ((original != null && current < original) ||
      (compareAt != null && current < compareAt));
  if (!onSale) return { price: shown, strikethrough: null };

  const originalShown = formatMoney(
    originalPrice?.amount,
    originalPrice?.currency,
    locale,
  );
  const strikethrough =
    originalShown && originalShown !== shown
      ? originalShown
      : formatMoney(price?.compare_at_amount, price?.currency, locale);
  return { price: shown, strikethrough: strikethrough || null };
}
